import { Injectable, inject } from '@angular/core';
import { ConfigService } from './config.service';
import { SentryService } from './sentry.service';

/**
 * PinEncryptionService
 * Encrypts a PIN string using the RSA public key that ships in the runtime
 * config (`/api/config` → `server_public_key`), exposed via ConfigService.
 *
 * The key deliberately no longer comes from `environment.*.ts`: those files
 * carried a single shared key across test/staging/prod, so ciphertext produced
 * for one environment could not be decrypted by another backend — surfacing as
 * a misleading "PIN invalide". The config API is the single source of truth and
 * always returns the key matching the backend it was fetched from.
 */
@Injectable({ providedIn: 'root' })
export class PinEncryptionService {
  private readonly config = inject(ConfigService);
  private readonly sentry = inject(SentryService);

  /**
   * Encrypts a plain-text PIN using RSA-OAEP / SHA-256 via the Web Crypto API
   * and returns standard Base64-encoded ciphertext.
   *
   * Throws (and reports to Sentry) when the key is unavailable or encryption
   * fails, rather than returning '' — an empty `pin_code` reaches the backend
   * as a generic "PIN invalide", indistinguishable from a genuinely wrong PIN,
   * which is exactly the failure mode this replaces.
   */
  async encryptPin(pin: string): Promise<string> {
    // Ensure the runtime config is loaded. `load()` is idempotent: it returns
    // the cached result on success and retries after a prior failure. This also
    // removes any reliance on the fire-and-forget configResolver having already
    // completed before the user reaches the export flow.
    await this.config.load();

    const publicKey = this.config.serverPublicKey();
    if (!publicKey) {
      const err = new Error('PIN encryption failed: server_public_key missing from runtime config');
      this.sentry.captureException(err, { tags: { feature: 'pin-encryption' } });
      throw err;
    }

    try {
      // Strip PEM headers and all whitespace to get raw Base64
      const rawBase64 = publicKey.replaceAll(/\s+/g, '');

      // Decode Base64 → binary string → ArrayBuffer (SPKI DER format)
      const binaryStr = atob(rawBase64);
      const keyBytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        keyBytes[i] = binaryStr.codePointAt(i) ?? 0;
      }

      // Import the public key
      const cryptoKey = await crypto.subtle.importKey(
        'spki',
        keyBytes.buffer,
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false,
        ['encrypt'],
      );

      // Encrypt the PIN (UTF-8 encoded)
      const pinBytes = new TextEncoder().encode(pin);
      const encrypted = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, cryptoKey, pinBytes);

      // Convert ArrayBuffer → standard Base64 (with +, /, =)
      const encryptedBytes = new Uint8Array(encrypted);
      let binary = '';
      encryptedBytes.forEach((b) => (binary += String.fromCodePoint(b)));
      return btoa(binary);
    } catch (err) {
      this.sentry.captureException(err, { tags: { feature: 'pin-encryption' } });
      throw err instanceof Error ? err : new Error('PIN encryption failed');
    }
  }
}
